<!--
  Simple table for facility rule state objects
    First column: Text column
    Second column: Boolean column
    Third column: Text column
    Fourth column: Documentations

    Loop keys:
      i ... index of header
      j ... index of rule
-->
<template>
  <div class="rule-state-table">
    <!-- Readonly Table -->
    <v-data-table
        v-if="loaded && readOnly"
        hide-default-footer
        hide-default-header
        mobile-breakpoint="700"
    >
      <template v-slot:header>
        <thead>
        <tr class="d-none d-md-table-row">
          <th v-for="h in header" :key="h" class="d-none d-sm-table-cell">
            <h6 class="mb-0">{{ h }}</h6>
          </th>
        </tr>
        </thead>
      </template>
      <template v-slot:body>

        <!-- Mobile Table body -->
        <v-row class="d-md-none border-bottom-line" v-for="(rule, i) in rulesOrPictures" :key="i">
          <v-col>
            <v-row v-for="(h, j) in header" :key="j">
              <v-col cols="6">
                <p class="mb-0 font-weight-bold">{{ h }}</p>
              </v-col>
              <v-col cols="6" class="text-right" v-if="j === 0">
                {{ rule.translatedName }}
              </v-col>
              <v-col cols="6" class="text-right" v-else-if="j === 1 && !isPictureState">
                <v-checkbox disabled class="mt-0 right-checkbox" v-model="rule.success"/>
              </v-col>
              <v-col cols="6" class="text-right" v-else-if="j === 2 && !isPictureState">
                {{ rule.note }}
              </v-col>
              <v-col cols="12" class="text-right" v-else-if="j === 3 || (isPictureState && j === 1)">
                <v-carousel
                    height="300"
                    v-show="rule.documents.length > 0"
                    :class="getCarouselHeight(rule.documents.length, true)">
                  <v-carousel-item
                      v-for="docu in rule.documents"
                      :key="docu.id"
                  >
                    <div class="pdf-preview-wrapper-mobile" v-if="docu.fileType === 'application/pdf'">
                      <div class="pdf-preview-mobile" >
                        <pdf
                            ref="pdf"
                            :src="docu.src"
                            class="pdf-mobile"
                            @click.native="viewDocumentation(rule.documents, docu)"
                        >
                        </pdf>
                        <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                      </div>
                    </div>

                    <div class="img-preview-wrapper-mobile" v-else>
                      <div class="img-preview-mobile">
                        <v-img
                            :src="docu.src"
                            width="200"
                            @click="viewDocumentation(rule.documents, docu)"
                        />
                        <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                      </div>
                    </div>
                  </v-carousel-item>
                </v-carousel>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- NOT Mobile Table body -->
        <tbody>
        <tr v-for="(rule, i) in rulesOrPictures" :key="i" class="d-none d-md-table-row">
          <td class="d-table-row d-sm-table-cell">{{ rule.translatedName }}</td>
          <td v-if="!isPictureState" class="d-table-row d-sm-table-cell">
            <v-checkbox
                v-model="rule.success"
                disabled
            />
          </td>
          <td v-if="!isPictureState" class="d-table-row d-sm-table-cell">{{ rule.note }}</td>
          <td class="custom-column py-3">
            <v-carousel
                height="230"
                v-show="rule.documents.length > 0"
                :class="getCarouselHeight(rule.documents.length, false)">
              <v-carousel-item
                  v-for="docu in rule.documents"
                  :key="docu.id"
              >
                <div class="pdf-preview-wrapper" v-if="docu.fileType === 'application/pdf'">
                  <div class="pdf-preview" >
                    <pdf
                        ref="pdf"
                        :src="docu.src"
                        class="pdf-mobile"
                        @click.native="viewDocumentation(rule.documents, docu)"
                    >
                    </pdf>
                    <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                  </div>
                </div>

                <div class="img-preview-wrapper" v-else>
                  <div class="img-preview">
                    <v-img
                        :src="docu.src"
                        width="150"
                        @click="viewDocumentation(rule.documents, docu)"
                    />
                    <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                  </div>
                </div>
              </v-carousel-item>
            </v-carousel>
          </td>
        </tr>
        </tbody>
      </template>
    </v-data-table>

    <!-- Table with ability to fill in data -->
    <v-data-table
        v-else-if="loaded"
        hide-default-footer
        hide-default-header
        mobile-breakpoint="700"
    >
      <template v-slot:header>
        <thead>
        <tr class="d-none d-md-table-row">
          <th v-for="h in header" :key="h" class="d-none d-sm-table-cell">
            <h6 class="mb-0">{{ h }}</h6>
          </th>
        </tr>
        </thead>
      </template>
      <template v-slot:body>

        <!-- Mobile Table body -->
        <v-row class="d-md-none border-bottom-line" v-for="(rule, i) in rulesOrPictures" :key="i">
          <v-col>
            <v-row v-for="(h, j) in header" :key="j">
              <v-col cols="6" align-self="end">
                <p class="mb-0 font-weight-bold">{{ h }}</p>
              </v-col>
              <v-col cols="6" class="text-right" v-if="j === 0">
                {{ rule.translatedName }}
              </v-col>
              <v-col cols="6" class="text-right" v-else-if="j === 1 && !isPictureState">
                <v-checkbox
                    class="mt-0 right-checkbox"
                    v-model="rule.success"
                    hide-details
                />
              </v-col>
              <v-col cols="6" class="text-right" v-else-if="j === 2 && !isPictureState">
                <v-text-field
                    class="pt-0"
                    v-model="rule.note"
                    :label="$t('common:comment')"
                    hide-details
                />
              </v-col>
              <v-col cols="6" class="text-right" v-else-if="j === 3 || (isPictureState && j === 1)">
                <v-btn icon color="primary" @click="openMobileCamera(rule)">
                  <v-icon>add_a_photo</v-icon>
                </v-btn>
                <DocumentationUploadForm
                    :ref="'docuUploadForm_' + i"
                    color="primary"
                    noBtnText
                    @postFile="postDocumentation($event, rule, facilityState)"
                    :presetDocuName="rule.translatedName + '_' + (rule.documents.filter(e => !e.toDelete).length + 1)"
                />
              </v-col>
              <v-col cols="12" class="text-right" v-else-if="rule.documents && (j === 4 || (isPictureState && j === 2))">
                <v-carousel
                    height="300"
                    v-show="rule.documents.filter(e => !e.toDelete).length > 0"
                    :class="getCarouselHeight(rule.documents.length, true)">
                  <v-carousel-item
                      v-for="docu in rule.documents.filter(e => !e.toDelete)"
                      :key="docu.id"
                  >
                    <div class="pdf-preview-wrapper-mobile" v-if="docu.fileType === 'application/pdf'">
                      <v-btn icon color="scaleRed" @click="deleteDocumentation(docu.localId, rule, 'dialogDeleteDocumentation_Mobile_' + i)">
                        <v-icon>
                          close
                        </v-icon>
                      </v-btn>
                      <div class="pdf-preview-mobile" >
                        <pdf
                            ref="pdf"
                            :src="docu.src"
                            class="pdf-mobile"
                            @click.native="viewDocumentation(rule.documents, docu)"
                        >
                        </pdf>
                        <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                      </div>
                    </div>

                    <div class="img-preview-wrapper-mobile" v-else>
                      <v-btn icon color="scaleRed" @click="deleteDocumentation(docu.localId, rule, 'dialogDeleteDocumentation_Mobile_' + i)">
                        <v-icon>
                          close
                        </v-icon>
                      </v-btn>
                      <div class="img-preview-mobile">
                        <v-img
                            :src="docu.src"
                            width="200"
                            @click="viewDocumentation(rule.documents, docu)"
                        />
                        <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                      </div>
                    </div>

                    <TwoButtonDialog
                        :ref="'dialogDeleteDocumentation_Mobile_' + i"
                        hideButton
                        :btn-text="$t('buttons:delete')" color="error"
                        :dialog-title="$t('dialog:delete-btn-title', {name : $t('book/public:qrcode.onboarding.dialogs.documentation-1')})"
                        :dialog-text="$t('dialog:delete-btn-dialog', {name : $t('book/public:qrcode.onboarding.dialogs.documentation-2')})"
                        :button1-text="$t('buttons:cancel')"
                        :button2-text="$t('buttons:delete')"
                        @clickedButton1="$refs['dialogDeleteDocumentation_' + i][0].showDialog = false"
                        @clickedButton2="deleteDocumentationConfirmed(rule, 'dialogDeleteDocumentation_Mobile_' + i)"
                    />

                  </v-carousel-item>
                </v-carousel>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- NOT Mobile Table body -->
        <tbody>
        <tr v-for="(rule, i) in rulesOrPictures" :key="i" class="d-none d-md-table-row">
          <td class="d-table-row d-sm-table-cell">{{ rule.translatedName }}</td>
          <td v-if="!isPictureState" class="d-table-row d-sm-table-cell">
            <v-checkbox
                v-model="rule.success"
            />
          </td>
          <td v-if="!isPictureState" class="d-table-row d-sm-table-cell">
            <v-text-field
                class="pt-0"
                v-model="rule.note"
                :label="$t('common:comment')"
                hide-details
            />
          </td>
          <td>
            <CameraUploadForm
                :ref="'cameraUploadForm_' + i"
                color="primary"
                noBtnText
                @postFile="postDocumentation($event, rule, facilityState)"
                :presetDocuName="rule.translatedName + '_' + (rule.documents.filter(e => !e.toDelete).length + 1)"
            />
            <DocumentationUploadForm
                :ref="'docuUploadForm_' + i"
                color="primary"
                noBtnText
                @postFile="postDocumentation($event, rule, facilityState)"
                :presetDocuName="rule.translatedName + '_' + (rule.documents.filter(e => !e.toDelete).length + 1)"
            />
          </td>
          <td class="custom-column py-3" v-if="rule.documents">
            <v-carousel
                height="230"
                v-show="rule.documents.filter(e => !e.toDelete).length > 0"
                :class="getCarouselHeight(rule.documents.length, false)">
              <v-carousel-item
                  v-for="docu in rule.documents.filter(e => !e.toDelete)"
                  :key="docu.id"
              >
                <div class="pdf-preview-wrapper" v-if="docu.fileType === 'application/pdf'">
                  <v-btn icon color="scaleRed" @click="deleteDocumentation(docu.localId, rule, 'dialogDeleteDocumentation_' + i)">
                    <v-icon>
                      close
                    </v-icon>
                  </v-btn>
                  <div class="pdf-preview" >
                    <pdf
                        ref="pdf"
                        :src="docu.src"
                        class="pdf-mobile"
                        @click.native="viewDocumentation(rule.documents, docu)"
                    >
                    </pdf>
                    <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                  </div>
                </div>

                <div class="img-preview-wrapper" v-else>
                  <v-btn icon color="scaleRed" @click="deleteDocumentation(docu.localId, rule, 'dialogDeleteDocumentation_' + i)">
                    <v-icon>
                      close
                    </v-icon>
                  </v-btn>
                  <div class="img-preview">
                    <v-img
                        :src="docu.src"
                        width="150"
                        @click="viewDocumentation(rule.documents, docu)"
                    />
                    <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                  </div>
                </div>

                <TwoButtonDialog
                    :ref="'dialogDeleteDocumentation_' + i"
                    hideButton
                    :btn-text="$t('buttons:delete')" color="error"
                    :dialog-title="$t('dialog:delete-btn-title', {name : $t('book/public:qrcode.onboarding.dialogs.documentation-1')})"
                    :dialog-text="$t('dialog:delete-btn-dialog', {name : $t('book/public:qrcode.onboarding.dialogs.documentation-2')})"
                    :button1-text="$t('buttons:cancel')"
                    :button2-text="$t('buttons:delete')"
                    @clickedButton1="$refs['dialogDeleteDocumentation_' + i][0].showDialog = false"
                    @clickedButton2="deleteDocumentationConfirmed(rule, 'dialogDeleteDocumentation_' + i)"
                />

              </v-carousel-item>
            </v-carousel>
          </td>
        </tr>
        </tbody>
      </template>
    </v-data-table>

    <!-- Additional Components -->
    <DocumentViewer ref="documentViewer" offline/>

    <MobileCameraUploadForm
        ref="mobileCameraUploadForm"
        color="primary"
        noBtnText
        @postPhotos="postPhotos($event, facilityState)"
        offline-mode
    />
  </div>
</template>
<script>
import CameraUploadForm from "@/modules/core/components/CameraUploadForm";
import DocumentationUploadForm from "@/modules/core/components/DocumentationUploadForm";
import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog";
import Utils from "@/modules/core/services/utils";
import Documentation from "@/modules/book/data/documentation";
import indexedDBService from '@/modules/core/services/indexedDB-service';
import Vue from "vue";
import MobileCameraUploadForm from "@/modules/core/components/MobileCameraUploadForm";

export default {
  name: "StateTable",
  props: {
    header: Array, // header translation for table
    readOnly: Boolean, // if table is readonly or if values can be inserted,
    isPictureState: Boolean, // if it is just "Picture mode" (so only two columns)
  },
  components: {
    MobileCameraUploadForm,
    TwoButtonDialog,
    DocumentationUploadForm,
    CameraUploadForm,
    DocumentViewer: () => import('@/modules/core/components/DocumentViewer/DocumentViewer'),
    pdf: () => import('vue-pdf')
  },
  data() {
    return {
      loaded: false,

      // delete object which includes local docu id and rule from where docu should be deleted
      deleteDocu: {
        docuId: null,
        rule: null
      },
      rulesOrPictures: [],
      facilityState: null
    }
  },
  watch: {
    // if data array is loaded
    rulesOrPictures() {
      if (this.rulesOrPictures && this.rulesOrPictures.length > 0) {
        this.loaded = true;
      }
    }
  },
  methods: {
    viewDocumentation(documents, data) {
      this.$refs.documentViewer.initViewer(documents, data.fileName)
    },

    // if user wants to delete a documentation
    // puffer the localId of the documentation and the rule
    deleteDocumentation(docuLocalId, rule, ref) {
      this.deleteDocu.docuId = docuLocalId;
      this.deleteDocu.rule = rule;
      this.$refs[ref][0].showDialog = true;
    },

    // if delete was confirmed
    deleteDocumentationConfirmed(rule, ref) {

      // close dialog
      this.$refs[ref][0].setButtonSettings(false, false, false, false);
      this.$refs[ref][0].showDialog = false;

      for (let i = 0; i < this.rulesOrPictures.length; i++) {
        if (this.rulesOrPictures[i].name === this.deleteDocu.rule.name) {

          // set the property 'toDelete' to true for the document
          // which should be deleted when everything will be posted
          for (let j = 0; j < this.rulesOrPictures[i].documents.length; j++) {
            if (this.rulesOrPictures[i].documents[j].localId === this.deleteDocu.docuId) {
              Vue.set(this.rulesOrPictures[i].documents[j], 'toDelete', true);
              Vue.set(this.rulesOrPictures[i].documents[j], 'toSave', false);
            }
          }

          if (!this.isPictureState) {
            // if all documents in the rules object has 'toDelete' set to true
            // set rule success to false
            let ruleDocuLength = 0;
            for (let j = 0; j < this.rulesOrPictures[i].documents.length; j++) {
              if ('toDelete' in this.rulesOrPictures[i].documents[j] && this.rulesOrPictures[i].documents[j].toDelete) {
                ruleDocuLength++;
              }

              if (ruleDocuLength === this.rulesOrPictures[i].documents.length) {
                this.rulesOrPictures[i].success = false;
              }
            }
          }
        }
      }

      // save the facility state, so on page refresh, we still know which docu to delete
      indexedDBService.saveFacilityState(this.facilityState)
        .then(() => { this.deleteDocumentationCleanup(); })
        .catch(() => { Utils.showFailureSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('common:documentation')})); })
    },

    deleteDocumentationCleanup() {
      this.deleteDocu.docuId = null;
      this.deleteDocu.rule = null;

      Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('common:documentation')}));
    },

    // Only mobile
    postPhotos(response, facilityState) {
      let existingDocuments = response.rowToPut.documents;
      let newDocuments = response.documents;
      if(!existingDocuments) {
        existingDocuments = newDocuments;
      } else {
        for (let i = 0; i < newDocuments.length; i++) {
          existingDocuments.push(newDocuments[i]);
        }
      }

      if (existingDocuments.length > 0) {
        response.rowToPut.success = true;
      }

      // save every added documentation, so it will be still there on page refresh
      indexedDBService.saveFacilityState(facilityState)
    },

    postDocumentation(args, rule, facilityState) {
      let reader = new FileReader();

      // have to define needed vars here, because otherwise
      // we can't access e.g. args.documentation.start inside
      // the load eventListener
      const startDate = args.documentation.start;
      const endDate = args.documentation.end;
      const name = args.documentation.name;
      const file = args.file;

      // add event listener to reader, so when readAsDataURL is finished
      // the code inside event listener will be executed and we can read
      // the source of the document via reader.result
      reader.addEventListener("load", function () {
        let document = new Documentation();

        if (startDate == null) {
          document.start = '';
        } else {
          document.start = startDate;
        }

        if (endDate == null) {
          document.end = '';
        } else {
          document.end = endDate;
        }

        document.src = reader.result;
        document.name = name;
        document.type = rule.name;
        document.file = file;
        document.toSave = true;
        document.fileType = file.type;

        // define a unique id, so the delete handling will be easier
        document.localId = Utils.generateUUIDv4();

        if(!rule.documents) {
          rule.documents = [];
        }

        rule.documents.push(document)
        if (rule.documents.length > 0) {
          rule.success = true;
        }

        // save every added documentation, so it will be still there on page refresh
        indexedDBService.saveFacilityState(facilityState)
      }, false);

      // if there is a file, read the content of the file
      // to display the document without the backend
      if (file) {
        reader.readAsDataURL(file);
      }
    },

    getCarouselHeight(elementCount, isMobile) {
      if (isMobile) {
        if (elementCount <= 9) {
          return 'carousel-control-small--mobile'
        } else {
          return 'carousel-control-normal--mobile'
        }
      } else {
        if (elementCount <= 6) {
          return 'carousel-control-small'
        } else if (elementCount > 6 && elementCount <= 12) {
          return 'carousel-control-normal'
        } else if (elementCount > 12) {
          return 'carousel-control-big'
        }
      }
    },

    openMobileCamera(rule) {
      let presetDocuName = rule.translatedName + '_' + (rule.documents.filter(e => !e.toDelete).length + 1);
      let documentationType = rule.name;
      this.$refs.mobileCameraUploadForm.openCameraDialog(presetDocuName, documentationType, rule)
    }
  }
}
</script>
<style lang="scss">
.rule-state-table {
  .right-checkbox {
    .v-input__control {
      .v-input__slot {
        justify-content: flex-end;
        margin-bottom: 0;
      }
    }
  }
  .border-bottom-line {
    box-shadow: 0 2px 1px 0 #e8eafc, 0 1px 1px -2px #b2b2b21a, 0 1px 1px 0 #9a9a9a1a;
  }

  /**
   ****** Vuetify Carousel CSS changes ******
   */
  // left btn smaller
  .v-window__prev {
    .v-btn {
      height: 24px !important;
      width: 24px !important;

      .v-icon {
        font-size: 24px !important;
      }
    }
  }

  // right btn smaller
  .v-window__next {
    .v-btn {
      height: 24px !important;
      width: 24px !important;

      .v-icon {
        font-size: 24px !important;
      }
    }
  }

  // Bottom control panel smaller
  // if elements are under 6
  .carousel-control-small {
    .v-carousel__controls {
      height: 30px!important;
    }
  }
  // if there are more than 6 elements
  .carousel-control-normal {
    .v-carousel__controls {
      height: 55px!important;
    }
  }

  // if there are more than 12 elements
  .carousel-control-big {
    .v-carousel__controls {
      height: 75px!important;
    }
  }

  /**
   ****** Normal screen sizes and up ******
   */
  .custom-column {
    width: 300px;
  }

  .pdf-preview-wrapper {
    position: relative;
    width: 100px;
    margin-left: auto;
    margin-right: auto;

    button {
      position: absolute;
      z-index: 1;
      right: 0;
    }

    .pdf-preview {
      border: 1px solid #ddd; /* Gray border */
      border-radius: 4px; /* Rounded border */
      padding: 5px; /* Some padding */
      width: 100px;
      cursor: pointer;

      .pdf-mobile {
        width: auto;
      }
    }
  }

  .img-preview-wrapper {
    position: relative;
    width: 150px;
    margin-left: auto;
    margin-right: auto;
    margin-top: 20px;

    button {
      position: absolute;
      z-index: 1;
      right: 0;
    }

    .img-preview {
      border: 1px solid #ddd; /* Gray border */
      border-radius: 4px; /* Rounded border */
      padding: 5px; /* Some padding */
      width: 150px;
      cursor: pointer;
    }
  }

  /**
   ****** MOBILE ******
   */
  .pdf-preview-wrapper-mobile {
    position: relative;
    width: 150px;
    margin-left: auto;
    margin-right: auto;

    button {
      position: absolute;
      z-index: 1;
      right: 0;
    }

    .pdf-preview-mobile {
      border: 1px solid #ddd; /* Gray border */
      border-radius: 4px; /* Rounded border */
      padding: 5px; /* Some padding */
      width: 150px;
      cursor: pointer;

      .pdf-mobile {
        width: auto;
      }
    }
  }

  .img-preview-wrapper-mobile {
    position: relative;
    width: 200px;
    margin-left: auto;
    margin-right: auto;

    button {
      position: absolute;
      z-index: 1;
      right: 0;
    }

    .img-preview-mobile {
      border: 1px solid #ddd; /* Gray border */
      border-radius: 4px; /* Rounded border */
      padding: 5px; /* Some padding */
      width: 200px;
      cursor: pointer;
    }
  }

  // Bottom carousel control panel smaller
  // if elements are under 6
  .carousel-control-small--mobile {
    .v-carousel__controls {
      height: 30px!important;
    }
  }
  // if there are more than 6 elements
  .carousel-control-normal--mobile {
    .v-carousel__controls {
      height: 55px!important;
    }
  }

  // if there are more than 12 elements
  .carousel-control-big--mobile {
    .v-carousel__controls {
      height: 55px!important;
    }
  }
}
</style>